// src/pages/Login.tsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth, RequiredLegalDocument } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Mail,
  Lock,
  AlertCircle,
  Loader,
  Eye,
  EyeOff,
  KeyRound,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  FileText,
  Calendar,
  CheckCircle2,
  Copy,
} from 'lucide-react';
import { toast } from "react-toastify";
import { MESSAGES } from '../utils/messages';
import {
  renderLegalContent,
  formatDocType,
  formatLegalDate,
} from '../components/Legal/LegalContentViewer';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateLoginForm = (email: string, password: string): string | null => {
  const errors: string[] = [];
  if (!email.trim()) {
    errors.push('Email is required');
  } else if (!EMAIL_REGEX.test(email.trim())) {
    errors.push('Enter a valid email address');
  }

  if (!password) {
    errors.push('Password is required');
  }

  return errors.length > 0 ? errors.join('. ') + '.' : null;
};

const CANONICAL_DOC_ORDER: Record<string, number> = {
  TERMS_AND_CONDITIONS: 1,
  PRIVACY_POLICY: 2,
  MEDICAL_DISCLAIMER: 3,
};

const sortLegalDocs = (docs: RequiredLegalDocument[]): RequiredLegalDocument[] => {
  if (!Array.isArray(docs)) return [];
  return [...docs].sort((a, b) => {
    const orderA = CANONICAL_DOC_ORDER[a.documentType] ?? 99;
    const orderB = CANONICAL_DOC_ORDER[b.documentType] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return (a.documentType || '').localeCompare(b.documentType || '');
  });
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { loginDoctor, loginAdmin, verifyMfaOtp, resendMfaOtp, completeLegalLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminLogin = location.pathname === '/admin/login';

  // ── Pre-login legal acceptance state ──────────────────────
  const [legalMode, setLegalMode] = useState(false);
  const [legalToken, setLegalToken] = useState<string>('');
  const [requiredLegalDocs, setRequiredLegalDocs] = useState<RequiredLegalDocument[]>([]);
  const [activeLegalIndex, setActiveLegalIndex] = useState(0);
  const [acceptedDocIds, setAcceptedDocIds] = useState<{ [docId: string]: boolean }>({});
  const [isSubmittingLegal, setIsSubmittingLegal] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<string>('/dashboard');

  // ── MFA state ──────────────────────────────────────────────
  const [mfaMode, setMfaMode] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expirySeconds, setExpirySeconds] = useState(0);
  const [devOtp, setDevOtp] = useState('');
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mfaMode) {
      otpInputRef.current?.focus();
    }
  }, [mfaMode]);

  useEffect(() => {
    const message = (location.state as { message?: string })?.message;
    if (message) {
      toast.info(message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // OTP expiry countdown
  useEffect(() => {
    if (expirySeconds <= 0) return;
    const timer = setInterval(() => {
      setExpirySeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [expirySeconds]);

  const enterMfaMode = (token: string, expiry: number, devOtpCode?: string) => {
    setMfaToken(token);
    setOtp('');
    setOtpError('');
    setExpirySeconds(expiry);
    setResendCooldown(60);
    setError('');
    setDevOtp(devOtpCode || '');
    setMfaMode(true);
  };

  const exitMfaMode = () => {
    setMfaMode(false);
    setMfaToken('');
    setOtp('');
    setOtpError('');
    setExpirySeconds(0);
    setResendCooldown(0);
    setDevOtp('');
    setPassword('');
  };

  const enterLegalMode = (
    token: string,
    docs: RequiredLegalDocument[],
    targetUrl: string = '/dashboard'
  ) => {
    setLegalToken(token);
    setRequiredLegalDocs(sortLegalDocs(docs));
    setActiveLegalIndex(0);
    setAcceptedDocIds({});
    setPendingTarget(targetUrl);
    setLegalMode(true);
    setMfaMode(false);
    setError('');
    setIsLoading(false);
  };

  const exitLegalMode = () => {
    setLegalMode(false);
    setLegalToken('');
    setRequiredLegalDocs([]);
    setActiveLegalIndex(0);
    setAcceptedDocIds({});
    setError('');
    setPassword('');
    setIsLoading(false);
  };

  const handleToggleDocAccepted = (docId: string, checked: boolean) => {
    setAcceptedDocIds((prev) => ({
      ...prev,
      [docId]: checked,
    }));
    setError('');
  };

  const handleAcceptAllLegal = () => {
    if (allLegalAccepted) {
      setAcceptedDocIds({});
    } else {
      const allAccepted: { [id: string]: boolean } = {};
      requiredLegalDocs.forEach((d) => {
        allAccepted[d.id] = true;
      });
      setAcceptedDocIds(allAccepted);
    }
    setError('');
  };

  const allLegalAccepted = useMemo(() => {
    if (requiredLegalDocs.length === 0) return false;
    return requiredLegalDocs.every((doc) => !!acceptedDocIds[doc.id]);
  }, [requiredLegalDocs, acceptedDocIds]);

  const acceptedLegalCount = useMemo(() => {
    return requiredLegalDocs.filter((doc) => !!acceptedDocIds[doc.id]).length;
  }, [requiredLegalDocs, acceptedDocIds]);

  const unacceptedLegalDocs = useMemo(() => {
    return requiredLegalDocs.filter((doc) => !acceptedDocIds[doc.id]);
  }, [requiredLegalDocs, acceptedDocIds]);

  const handleAcceptLegalTerms = async () => {
    if (!allLegalAccepted || isSubmittingLegal) {
      if (!allLegalAccepted) {
        const remaining = unacceptedLegalDocs.map((d) => d.title).join(", ");
        setError(`All updated legal documents must be accepted before logging in. Please accept: ${remaining}`);
      }
      return;
    }

    setIsSubmittingLegal(true);
    setError('');

    try {
      const acceptedIds = Object.keys(acceptedDocIds).filter((id) => acceptedDocIds[id]);
      const result = await completeLegalLogin(legalToken, acceptedIds);

      if (result.success) {
        toast.success(MESSAGES.success.login);
        setLegalMode(false);
        setIsSubmittingLegal(false);
        if (result.isValidated === false) {
          return navigate("/set-password");
        }
        return navigate(pendingTarget || "/dashboard");
      } else {
        const msg = result.errorMessage || "Failed to complete legal acceptance login. Please try again.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err: any) {
      console.error("Legal acceptance login error:", err);
      const msg = err.response?.data?.message || "Failed to complete legal acceptance login. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmittingLegal(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mfaMode) {
      await handleVerifyOtp();
      return;
    }

    const validationError = validateLoginForm(email, password);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const result = isAdminLogin
        ? await loginAdmin(email, password)
        : await loginDoctor(email, password);

      if (result.success && result.requiresMfa && result.mfaToken) {
        enterMfaMode(result.mfaToken, result.mfa?.expirySeconds || 300, result.mfa?.otp);
        toast.info("A verification code has been sent to your email.");
        setIsLoading(false);
        return;
      }

      if (!isAdminLogin && result.success && result.requiresLegalAcceptance && result.legalToken) {
        enterLegalMode(
          result.legalToken,
          result.requiredDocuments || [],
          result.needsPasswordReset ? "/set-password" : "/dashboard"
        );
        setIsLoading(false);
        return;
      }

      if (result.success) {
        if (result.needsPasswordReset) {
          toast.info(MESSAGES.info.needsPasswordReset);
          setIsLoading(false);
          return navigate("/set-password");
        } else {
          toast.success(isAdminLogin ? MESSAGES.success.adminLogin : MESSAGES.success.login);
          setIsLoading(false);
          return navigate("/dashboard");
        }
      } else {
        const errorMessage = result.errorMessage || MESSAGES.error.invalidCredentials;
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err: any) {
      setPassword('');
      setError(MESSAGES.error.credentialsError);
      toast.error(MESSAGES.error.credentialsError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = otp.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setOtpError("Enter the 6-digit code from your email.");
      return;
    }

    setOtpError('');
    setError('');
    setIsLoading(true);
    try {
      const result = await verifyMfaOtp(mfaToken, trimmed);

      if (!isAdminLogin && result.success && result.requiresLegalAcceptance && result.legalToken) {
        setMfaMode(false);
        enterLegalMode(
          result.legalToken,
          result.requiredDocuments || [],
          result.isValidated === false ? "/set-password" : "/dashboard"
        );
        setIsLoading(false);
        return;
      }

      if (result.success) {
        setMfaMode(false);
        toast.success(MESSAGES.success.login);
        setIsLoading(false);
        if (result.isValidated === false) {
          return navigate("/set-password");
        }
        return navigate("/dashboard");
      }
      const errorMessage = result.errorMessage || "Invalid verification code.";
      setError(errorMessage);
      toast.error(errorMessage);
      setOtp('');
    } catch (err: any) {
      setError(MESSAGES.error.invalidOrExpiredOtp || "Verification failed. Please try again.");
      toast.error(MESSAGES.error.invalidOrExpiredOtp || "Verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setOtpError('');
    setError('');
    setIsLoading(true);
    try {
      const result = await resendMfaOtp(mfaToken);
      if (result.success) {
        setOtp('');
        setExpirySeconds(result.mfa?.expirySeconds || 300);
        setResendCooldown(60);
        if (result.mfa?.otp) setDevOtp(result.mfa.otp);
        toast.success("A new verification code has been sent.");
      } else {
        const errorMessage = result.errorMessage || "Failed to resend the verification code.";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err: any) {
      setError("Failed to resend the verification code.");
      toast.error("Failed to resend the verification code.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative w-screen min-h-screen md:h-screen flex flex-col md:flex-row overflow-x-hidden md:overflow-hidden bg-white">
      {/* Asymmetric Curved Divider SVG (Visible on desktop/tablet) */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-[1] hidden md:block" aria-hidden="true">
        <svg className="w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="none">
          <defs>
            <linearGradient id="portalTealGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#26ABB0" />
              <stop offset="100%" stopColor="#219FA4" />
            </linearGradient>
          </defs>
          <path
            d="M 526.3 0 C 464.5 333.3, 475.4 666.7, 602.3 1000 L 1000 1000 L 1000 0 Z"
            fill="url(#portalTealGrad)"
          />
        </svg>
      </div>

      {/* LEFT PANEL: Branding & Decorative Healthcare Graphics */}
      <div className="relative w-full md:w-1/2 h-auto md:h-full flex flex-col justify-between px-6 py-4 sm:p-11 lg:p-14 z-[2] bg-white md:bg-transparent shrink-0">
        {/* Center Branding using original SVG logo */}
        <div className="my-3 sm:my-4 md:my-0 md:absolute md:top-[43%] md:left-[50%] md:-translate-x-1/2 md:-translate-y-1/2 flex flex-col items-center justify-center text-center w-full max-w-[90%] md:max-w-[720px] lg:max-w-[820px] xl:max-w-[880px] px-2">
          <img
            src="/logo/DiagnomIQ-01.svg"
            alt="DiagnomIQ - Precision insights for every patient"
            className="w-full max-w-[340px] sm:max-w-[480px] md:max-w-[680px] lg:max-w-[780px] xl:max-w-[840px] h-auto object-contain"
          />
        </div>

        {/* Decorative Medical Graphic in Bottom-Left */}
        <svg
          className="hidden md:block absolute bottom-0 left-0 w-auto h-[48vh] max-h-[500px] pointer-events-none z-[1]"
          viewBox="0 0 380 500"
          fill="none"
          aria-hidden="true"
        >
          {/* Outer Stroke */}
          <path
            d="M 90 -20 L 126 15 Q 140 29 154 43 L 265 154 Q 283 172 283 198 L 283 310 Q 283 336 265 354 L 154 465 Q 140 479 126 493 L 90 528"
            stroke="#D2EEF1"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          {/* Pale Shape Fill */}
          <path
            d="M 0 57 L 61 57 Q 78 57 90 69 L 204 183 Q 222 201 222 225 L 222 297 Q 222 321 204 339 L 90 453 Q 78 465 61 465 L 0 465 Z"
            fill="#EDFAFB"
          />
          {/* White Cross */}
          <path
            d="M 0 133 L 46 133 Q 67 133 67 154 L 67 216 Q 67 234 85 234 L 138 234 Q 158 234 158 254 L 158 284 Q 158 304 138 304 L 85 304 Q 67 304 67 322 L 67 384 Q 67 405 46 405 L 0 405 Z"
            fill="#FFFFFF"
          />
        </svg>
      </div>

      {/* RIGHT PANEL: Teal Background & Login Form */}
      <div className="relative w-full md:w-1/2 flex-1 md:h-full flex items-center justify-center p-6 sm:p-10 lg:p-14 z-[2] bg-[#23A4A8] md:bg-transparent rounded-t-[32px] md:rounded-none shadow-lg md:shadow-none min-h-[520px]">
        {/* Top-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute top-0 right-0 w-[320px] lg:w-[450px] h-[320px] lg:h-[450px] pointer-events-none z-[1]"
          viewBox="0 0 450 450"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="492" cy="-25" r="225" fill="#2FAFB1" />
          <circle cx="492" cy="-25" r="162" fill="#48B9BC" />
        </svg>

        {/* Bottom-Right Decorative Arcs */}
        <svg
          className="hidden md:block absolute bottom-0 right-0 w-[380px] lg:w-[550px] h-[380px] lg:h-[550px] pointer-events-none z-[1]"
          viewBox="0 0 550 550"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="578" cy="651" r="298" fill="#33AAAD" />
          <circle cx="578" cy="651" r="244" fill="#44B6B9" />
        </svg>

        {/* Form Container Sitting Directly on Teal Background */}
        <div className="relative z-[3] w-full max-w-[465px]">
          {mfaMode ? (
            /* ── MFA Verification Step ───────────────────────── */
            <div className="space-y-5">
              <div className="space-y-3 text-center">
                <div className="flex justify-center mb-1">
                  <div className="inline-flex p-3.5 sm:p-4 rounded-full bg-white/20 text-white">
                    <ShieldCheck className="h-10 w-10 sm:h-12 sm:w-12" />
                  </div>
                </div>
                <h1 className="text-[26px] sm:text-[30px] font-bold text-white tracking-tight leading-tight">
                  Two-Factor Verification
                </h1>
                <p className="text-[14.5px] sm:text-[15.5px] text-[#E0F7F6] leading-relaxed">
                  A 6-digit verification code was sent to{' '}
                  <span className="font-semibold text-white">{email}</span>.
                  Enter it below to complete signing in.
                </p>
                {expirySeconds > 0 && (
                  <p className="text-[12.5px] sm:text-[13px] text-[#E0F7F6]/90 font-medium">
                    Code expires in <span className="font-semibold text-white">{expirySeconds}s</span>
                  </p>
                )}
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }} className="space-y-4 sm:space-y-5">
                <div>
                  <label htmlFor="otp" className="block text-[14.5px] sm:text-[15px] font-semibold text-white mb-2">
                    Verification Code
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <KeyRound className="h-5 w-5 text-[#94A3B8]" />
                    </div>
                    <input
                      id="otp"
                      name="otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pasted = e.clipboardData.getData('text');
                        const digits = pasted.replace(/\D/g, '').slice(0, 6);
                        if (digits) {
                          setOtp(digits);
                        }
                      }}
                      ref={otpInputRef}
                      className="w-full h-[54px] sm:h-[56px] bg-white rounded-md pl-12 pr-4 text-center text-lg font-mono tracking-[0.3em] text-[#1E293B] placeholder-[#94A3B8] outline-none shadow-sm focus:ring-2 focus:ring-[#097173]"
                      placeholder="••••••"
                    />
                  </div>
                  {otpError && <p className="mt-1.5 text-xs sm:text-sm text-red-100 font-medium">{otpError}</p>}
                </div>

                {error && (
                  <div className="flex items-center space-x-2 text-white bg-red-600/40 border border-red-300/40 p-3 rounded-md text-sm">
                    <AlertCircle className="h-5 w-5 shrink-0 text-red-200" />
                    <span>{error}</span>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-[54px] sm:h-[56px] bg-[#097173] hover:bg-[#075E60] active:scale-[0.99] text-white font-semibold text-[16px] rounded-md flex items-center justify-center space-x-2 shadow-lg transition-all duration-150 disabled:opacity-60 cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <Loader className="h-5 w-5 mr-2 animate-spin" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <span>Verify &amp; Sign In</span>
                        <ArrowRight className="h-5 w-5 ml-1" />
                      </>
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0 || isLoading}
                    className="text-[14px] font-medium text-white hover:text-[#D8F2F1] underline disabled:opacity-50 disabled:no-underline cursor-pointer transition-colors"
                  >
                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                  </button>

                  <button
                    type="button"
                    onClick={exitMfaMode}
                    className="inline-flex items-center text-[14px] font-medium text-white/90 hover:text-white cursor-pointer transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to login
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* ── Normal Login Form (Exact Visual Match to Reference) ───────── */
            <>
              <h1 className="text-[30px] sm:text-[34px] font-bold text-white tracking-tight leading-tight mb-2">
                Welcome Back
              </h1>
              <p className="text-[14.5px] sm:text-[15.5px] font-normal text-[#E0F7F6] mb-7 leading-relaxed">
                {isAdminLogin
                  ? "Sign in with your administrator credentials"
                  : "Sign in to access your personalized precision care treatment system"}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-[14.5px] sm:text-[15px] font-semibold text-white mb-1.5">
                    Email
                  </label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-[#94A3B8] pointer-events-none flex items-center" aria-hidden="true">
                      <Mail className="h-5 w-5" />
                    </span>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full h-[54px] sm:h-[56px] bg-white rounded-md pl-12 pr-4 text-[15px] text-[#1E293B] placeholder-[#94A3B8] outline-none shadow-sm transition-all focus:ring-2 focus:ring-[#097173] border border-white/40"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-[14.5px] sm:text-[15px] font-semibold text-white mb-1.5">
                    Password
                  </label>
                  <div className="relative flex items-center">
                    <span className="absolute left-4 text-[#94A3B8] pointer-events-none flex items-center" aria-hidden="true">
                      <Lock className="h-5 w-5" />
                    </span>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="w-full h-[54px] sm:h-[56px] bg-white rounded-md pl-12 pr-12 text-[15px] text-[#1E293B] placeholder-[#94A3B8] outline-none shadow-sm transition-all focus:ring-2 focus:ring-[#097173] border border-white/40"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 text-[#94A3B8] hover:text-[#64748B] focus:outline-none p-1 transition-colors cursor-pointer"
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Forgot Password Link (Aligned Right) */}
                <div className="flex justify-end pt-0.5">
                  <Link
                    to="/forgot-password"
                    className="text-white text-[14px] sm:text-[14.5px] font-medium hover:text-[#E0F7F6] hover:underline transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Error Banner */}
                {error && (
                  <div className="flex items-center space-x-2 text-white bg-red-600/40 border border-red-300/40 p-3 rounded-md text-sm">
                    <AlertCircle className="h-5 w-5 shrink-0 text-red-200" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-1.5">
                  <button
                    type="submit"
                    disabled={isLoading}
                    aria-label="Sign in"
                    className="w-full h-[54px] sm:h-[56px] bg-[#097173] hover:bg-[#075E60] active:scale-[0.99] text-white font-semibold text-[16px] sm:text-[16.5px] rounded-md flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <Loader className="h-5 w-5 mr-2 animate-spin" />
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <>
                        <span>{isAdminLogin ? "Sign In as Admin" : "Sign In"}</span>
                        <ArrowRight className="h-5 w-5 ml-1" />
                      </>
                    )}
                  </button>
                </div>

                {/* Footer links: Register and Legal documents (Normal login only) */}
                {!isAdminLogin && (
                  <div className="text-center space-y-3.5 pt-3.5">
                    <p className="text-[15px] sm:text-[15.5px] font-medium text-white/95">
                      Don't have an account?{' '}
                      <Link
                        to="/register"
                        className="font-bold text-white underline hover:text-[#D8F2F1] transition-colors ml-1"
                      >
                        Register
                      </Link>
                    </p>

                    <div className="pt-2.5 border-t border-white/25 flex flex-wrap items-center justify-center gap-x-3.5 gap-y-1 text-[13px] sm:text-[13.5px] font-medium text-white/85">
                      <Link
                        to="/legal?tab=terms-and-conditions"
                        className="hover:text-white hover:underline transition-colors"
                      >
                        Terms
                      </Link>
                      <span className="text-white/60 text-xs select-none">&bull;</span>
                      <Link
                        to="/legal?tab=privacy-policy"
                        className="hover:text-white hover:underline transition-colors"
                      >
                        Privacy
                      </Link>
                      <span className="text-white/60 text-xs select-none">&bull;</span>
                      <Link
                        to="/legal?tab=medical-disclaimer"
                        className="hover:text-white hover:underline transition-colors"
                      >
                        Medical Disclaimer
                      </Link>
                    </div>
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>

      {/* ── Pre-login Legal Acceptance Modal Dialog ──────────────────── */}
      {legalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white shadow-2xl rounded-xl border border-neutral-200 overflow-hidden w-full max-w-3xl my-8">
            {/* Modal Header */}
            <div className="p-6 border-b border-neutral-200 bg-neutral-50/60 flex items-start space-x-3">
              <div className="p-2 rounded-lg bg-primary-50 text-primary-600 mt-0.5">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-base sm:text-lg font-bold text-neutral-900">
                    Policy Acceptance Required
                  </h2>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold uppercase tracking-wider">
                    Action Required
                  </span>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  DiagnomIQ's legal documents have been updated. Before signing in, you must review and accept the current version to access your account.
                </p>
              </div>
            </div>

            {/* Tabs for required documents */}
            {requiredLegalDocs.length > 0 && (
              <div className="px-6 border-b border-neutral-200 bg-white flex space-x-2 overflow-x-auto">
                {requiredLegalDocs.map((doc, idx) => {
                  const isActive = idx === activeLegalIndex;
                  const isAccepted = !!acceptedDocIds[doc.id];
                  return (
                    <button
                      key={doc.id || doc.documentType}
                      type="button"
                      onClick={() => setActiveLegalIndex(idx)}
                      className={`py-3 px-3.5 text-xs font-medium border-b-2 flex items-center space-x-2 whitespace-nowrap transition-colors cursor-pointer ${
                        isActive
                          ? "border-primary-600 text-primary-600"
                          : "border-transparent text-neutral-500 hover:text-neutral-900 hover:border-neutral-300"
                      }`}
                    >
                      <FileText className={`h-4 w-4 ${isActive ? "text-primary-600" : "text-neutral-400"}`} />
                      <span>{formatDocType(doc.documentType)}</span>
                      {isAccepted ? (
                        <span className="ml-1 text-emerald-600 font-bold" title="Accepted">
                          ✓
                        </span>
                      ) : (
                        <span className="ml-1 text-amber-500 font-bold" title="Pending Acceptance">
                          *
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Current Document Metadata */}
            {requiredLegalDocs[activeLegalIndex] && (
              <div className="px-6 py-2.5 bg-neutral-50/70 border-b border-neutral-200 flex flex-wrap items-center justify-between text-xs text-neutral-600 gap-2">
                <span className="font-semibold text-neutral-900">
                  {requiredLegalDocs[activeLegalIndex].title}
                </span>
                <div className="flex items-center space-x-3 text-neutral-500">
                  {requiredLegalDocs[activeLegalIndex].effectiveFrom && (
                    <span className="inline-flex items-center">
                      <Calendar className="h-3.5 w-3.5 mr-1 text-neutral-400" />
                      Effective: {formatLegalDate(requiredLegalDocs[activeLegalIndex].effectiveFrom)}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Scrollable Document Text Content */}
            <div className="p-6 overflow-y-auto max-h-[40vh] bg-white text-sm leading-relaxed">
              {requiredLegalDocs[activeLegalIndex] ? (
                <div className="max-w-none">
                  {renderLegalContent(requiredLegalDocs[activeLegalIndex].content)}
                </div>
              ) : (
                <p className="text-neutral-500 text-center py-8">No content available.</p>
              )}
            </div>

            {/* Single checkbox acceptance toggle */}
            {requiredLegalDocs[activeLegalIndex] && (
              <div className="px-6 py-3 bg-neutral-50 border-t border-neutral-200 flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center space-x-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!acceptedDocIds[requiredLegalDocs[activeLegalIndex].id]}
                    onChange={(e) =>
                      handleToggleDocAccepted(
                        requiredLegalDocs[activeLegalIndex].id,
                        e.target.checked
                      )
                    }
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                  />
                  <span className="text-xs sm:text-sm text-neutral-800">
                    I have read and accept the{" "}
                    <strong className="font-semibold text-neutral-900">
                      {requiredLegalDocs[activeLegalIndex].title}
                    </strong>
                  </span>
                </label>
                <div className="flex items-center space-x-2 ml-auto">
                  {activeLegalIndex > 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveLegalIndex((prev) => prev - 1)}
                      className="text-xs text-neutral-600 hover:text-neutral-900 font-medium whitespace-nowrap cursor-pointer"
                    >
                      &larr; Previous
                    </button>
                  )}
                  {activeLegalIndex < requiredLegalDocs.length - 1 && (
                    <button
                      type="button"
                      onClick={() => setActiveLegalIndex((prev) => prev + 1)}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                    >
                      Next Document &rarr;
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Card Footer with Acceptance Actions */}
            <div className="p-6 border-t border-neutral-200 bg-neutral-50/80 space-y-4">
              <div className="space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-neutral-600">
                    Required Documents ({acceptedLegalCount} of {requiredLegalDocs.length} accepted)
                  </span>
                  <button
                    type="button"
                    onClick={handleAcceptAllLegal}
                    className="text-primary-600 hover:text-primary-800 font-medium cursor-pointer"
                  >
                    {allLegalAccepted ? "Deselect All" : "Accept All Required"}
                  </button>
                </div>

                {!allLegalAccepted && (
                  <div className="text-xs text-amber-800 bg-amber-50 p-2.5 rounded-lg border border-amber-200 flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-600" />
                    <span>
                      All new versions must be accepted before logging in. Still pending:{" "}
                      <strong className="font-semibold">
                        {unacceptedLegalDocs.map((d) => d.title).join(", ")}
                      </strong>
                    </span>
                  </div>
                )}
              </div>

              {error && (
                <div className="flex items-center space-x-2 text-xs sm:text-sm text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-200">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
                <button
                  type="button"
                  onClick={exitLegalMode}
                  disabled={isSubmittingLegal}
                  className="w-full sm:w-auto px-4 py-2.5 text-xs sm:text-sm font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors flex items-center justify-center space-x-1.5 cursor-pointer"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Cancel &amp; Sign Out</span>
                </button>

                <button
                  type="button"
                  onClick={handleAcceptLegalTerms}
                  disabled={!allLegalAccepted || isSubmittingLegal}
                  className="w-full sm:w-auto px-6 py-2.5 text-xs sm:text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm cursor-pointer"
                >
                  {isSubmittingLegal ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin mr-1.5" />
                      <span>Accepting &amp; Signing In...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      <span>
                        {allLegalAccepted
                          ? "Accept All & Complete Sign In"
                          : `Accept All (${acceptedLegalCount}/${requiredLegalDocs.length}) to Sign In`}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dev/QA OTP badge — only visible in non-production */}
      {mfaMode && devOtp && process.env.NODE_ENV !== 'production' && (
        <div
          className="fixed bottom-4 right-4 z-50 bg-neutral-900/95 text-white px-4 py-2.5 rounded-lg shadow-xl text-sm font-mono backdrop-blur-sm flex items-center space-x-3 border border-neutral-700 select-text"
          style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
        >
          <span className="opacity-70 select-text" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
            Dev OTP:
          </span>
          <span
            className="font-bold tracking-widest text-emerald-400 select-text cursor-text"
            style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
          >
            {devOtp}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(devOtp);
                toast.success('Dev OTP copied to clipboard');
              }
            }}
            className="p-1.5 hover:bg-neutral-800 rounded-md transition-colors text-white/80 hover:text-white cursor-pointer"
            title="Copy Dev OTP to clipboard"
            aria-label="Copy Dev OTP to clipboard"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Login;
